import React, { memo, useCallback, useMemo } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { Dropdown, Typography } from 'antd';
import { constants, actions } from '@amplement/backend-connector';
import { useDispatch } from 'react-redux';

import Button from 'components/Shared/Common/Button';
import { MAX_USER_DISPLAYED_ON_INCOMING } from 'const/call';
import IncomingCallLayout from 'components/Shared/Call/Notifications/IncomingCallLayout';
import AvatarList from 'components/Shared/Call/AvatarList';
import { getUrl } from 'utils/url';

import { StyledFastReply } from './styles';

const { AUDIO, VIDEO } = constants.rooms.ROOM_TYPE;
const { Text } = Typography;
const styleText = { fontSize: 9 };

type FastReplyDropDownProps = {
    _room: string;
} & WrappedComponentProps;

const FastReplyDropDown = injectIntl(memo(({ intl, _room }: FastReplyDropDownProps) => {
    const dispatch = useDispatch();
    
    const fastReplyMessages = useMemo(() => [
        intl.formatMessage({ id: 'room.actions.fastReplies.01' }),
        intl.formatMessage({ id: 'room.actions.fastReplies.02' }),
        intl.formatMessage({ id: 'room.actions.fastReplies.03' }),
        intl.formatMessage({ id: 'room.actions.fastReplies.04' })
    ], []);

    const handleFastMsg = useCallback((content: any) => () => {
        dispatch(actions.rooms.sendMessageToCaller(_room, content));
    }, [_room, dispatch]);

    const menu = useMemo(() => ({
        items: fastReplyMessages.map(message => ({
            key: message,
            onClick: handleFastMsg(message),
            label: message
        }))
    }), [fastReplyMessages]);

    return (
        <StyledFastReply>
            <Dropdown
                menu={menu}
                placement="top"
                arrow
                trigger={['click']}
            >
                <div className="button-container">
                    <Button
                        iconName="MessageSquare"
                        color="secondary"
                        size="md"
                    />
                    <Text
                        className="label"
                        style={styleText}
                        strong
                    >
                        {intl.formatMessage({ id: 'room.actions.chat' })}
                    </Text>
                </div>
            </Dropdown>
        </StyledFastReply>
    );
}));

type RoomIncomingCallProps = {
    _room: string;
        transferToPhonenumber: boolean;
        avatarUri: string;
        roomType: string;
        callerName: string;
        description: string;
        onReject: (id: string) => void;
        onClose: () => void;
        onIgnore: (id: string) => void;
        onAccept: (id: string, type: string) => void;
    isCrmData?: boolean;
    url?: string;
    source?: string;
    callerPhoneNumber?:string;
    members: object[];
    hasFastReply: boolean;
    transferredFrom?: string;
} & WrappedComponentProps

const RoomIncomingCall = (props: RoomIncomingCallProps) => {
    const {
        avatarUri = '',
        callerName = '',
        isCrmData = false,
        url,
        source,
        callerPhoneNumber = '',
        description,
        roomType = constants.rooms.ROOM_TYPE.AUDIO,
        onAccept,
        onReject,
        onIgnore,
        onClose,
        transferredFrom = '',
        intl,
        members,
        hasFastReply,
        _room,
        transferToPhonenumber = false
    } = props;
    const videoRoomUrl = getUrl('room', { _room, kind: VIDEO });
    const audioRoomUrl = getUrl('room', { _room });
    
    const handleAcceptAudio = useCallback(() => {
        onAccept(_room, AUDIO);
        onClose();
    }, [_room, onAccept]);

    const handleAcceptVideo = useCallback(() => {
        onAccept(_room, VIDEO);
        onClose();
    }, [_room, onAccept]);

    const handleReject = useCallback(() => {
        onReject(_room);
        onClose();
    }, [_room, onReject]);

    const handleIgnore = useCallback(() => {
        onIgnore(_room);
        onClose();
    }, [_room, onIgnore]);

    return (
        <IncomingCallLayout
            _room={_room}
            transferToPhonenumber={transferToPhonenumber}
            avatarUri={avatarUri}
            callerName={callerName}
            isCrmData={isCrmData}
            url={url}
            source={source}
            callerPhoneNumber={callerPhoneNumber}
            transferredFrom={transferredFrom}
            description={description}
            body={
                members?.length ? (
                    <div className="list-participants-container">
                        <div className="label">
                            {intl.formatMessage({ id: 'room.actions.memberList' })}
                        </div>
                        <AvatarList
                            members={members}
                            /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                            /* @ts-ignore */
                            hasMore
                            maxCount={MAX_USER_DISPLAYED_ON_INCOMING}
                        />
                    </div>
                ) : null
            }
            actions={(
                <>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore */}
                    {hasFastReply && <FastReplyDropDown _room={_room} />}
                    <div className="action-container-line align-end">
                        <div className="button-container">
                            <Button
                                iconName="Phone"
                                color="success"
                                animation="pulse"
                                href={audioRoomUrl}
                                target={`room-${_room}`}
                                onClick={handleAcceptAudio}
                            />
                            <Text
                                className="label"
                                style={styleText}
                                strong
                            >
                                {intl.formatMessage({ id: 'call.button.label.audio' })}
                            </Text>
                        </div>
                        {roomType === constants.rooms.ROOM_TYPE.VIDEO && (
                            <div className="button-container">
                                <Button
                                    iconName="Video"
                                    color="success"
                                    animation="pulse"
                                    href={videoRoomUrl}
                                    target={`room-${_room}`}
                                    onClick={handleAcceptVideo}
                                />
                                <Text
                                    className="label"
                                    style={styleText}
                                    strong
                                >
                                    {intl.formatMessage({ id: 'call.button.label.video' })}
                                </Text>
                            </div>
                        )}
                        <div className="button-container">
                            <Button
                                iconName="BellOff"
                                onClick={handleIgnore}
                                color="secondary"
                            />
                            <Text
                                className="label"
                                style={styleText}
                                strong
                            >
                                {intl.formatMessage({ id: 'global.button.ignore' })}
                            </Text>
                        </div>
                        <div className="button-container danger">
                            <Button
                                iconName="PhoneOff"
                                color="danger"
                                onClick={handleReject}
                            />
                            <Text
                                className="label"
                                style={styleText}
                                strong
                            >
                                {intl.formatMessage({ id: 'call.button.endCall' })}
                            </Text>
                        </div>
                    </div>
                </>
            )}
        />
    );
}

export default injectIntl(RoomIncomingCall);
